import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  PropsWithOptionalTestID,
  CoreProgressIndicator,
} from "@boi/components-core";
import { useDesignTokens } from "@boi/react-design-tokens";

import * as S from "./ProgressIndicator.styled";

export type ProgressIndicatorProps = PropsWithOptionalTestID & {
  totalSteps: number;
  currentStep: number;
  backgroundVariant?: CoreProgressIndicator.BackgroundVariant;
};

export const defaultTestID = "progress";

function ProgressIndicator({
  totalSteps,
  currentStep,
  backgroundVariant = CoreProgressIndicator.DEFAULT_VARIANT,
  testID = defaultTestID,
}: ProgressIndicatorProps) {
  const steps = useMemo(() => [...Array(totalSteps).keys()], [totalSteps]);

  const renderStep = useCallback(
    (item: number, index: number) => {
      return (
        <S.Segment
          key={item}
          data-testid={`${testID}-step-${item}`}
          $backgroundVariant={backgroundVariant}
          $isFirst={index === 0}
          $isLast={index === steps.length - 1}
          $isFilled={item < currentStep}
        />
      );
    },
    [currentStep, steps, testID]
  );

  return (
    <div
      data-testid={testID}
      role="group"
      aria-label={`Step ${currentStep} of ${totalSteps}`}
    >
      <ProgressIndicatorLabel testID={`${testID}-label`}>
        Step {currentStep}/{totalSteps}
      </ProgressIndicatorLabel>
      <S.Wrapper aria-hidden>{steps.map(renderStep)}</S.Wrapper>
    </div>
  );
}

function ProgressIndicatorLabel(
  props: PropsWithChildren & PropsWithOptionalTestID
) {
  const { tokens } = useDesignTokens();

  const tabletMatchMedia = window.matchMedia(
    `(min-width: ${tokens.breakpoint.tablet})`
  );

  const [largerThanMobile, setLargerThanMobile] = useState(() => {
    return tabletMatchMedia.matches;
  });

  useEffect(() => {
    function handleChange(event: MediaQueryListEvent) {
      setLargerThanMobile(event.matches);
    }

    tabletMatchMedia.addEventListener("change", handleChange);

    return () => {
      tabletMatchMedia.removeEventListener("change", handleChange);
    };
  }, []);

  const variant = largerThanMobile
    ? tokens.progressIndicator.label.text.typography.variant.tablet
    : tokens.progressIndicator.label.text.typography.variant.mobile;

  return (
    <S.Label
      testID={props.testID}
      variant={variant}
      fontFamily={tokens.progressIndicator.label.text.typography.fontFamily}
      aria-hidden
      children={props.children}
    />
  );
}

export default ProgressIndicator;
